import "@/App.css";
import {
    BrowserRouter,
    Routes,
    Route,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Home from "@/pages/Home";
import Apply from "@/pages/Apply";
import Status from "@/pages/Status";
import Admin from "@/pages/Admin";
import { completeAuthSession } from "@/lib/api";
import { Toaster } from "sonner";

function AuthHashHandler() {
    const location = useLocation();
    const navigate = useNavigate();
    const handled = useRef(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        const hash = location.hash || window.location.hash;
        const m = hash.match(/session_id=([^&]+)/);
        if (!m || handled.current) return;
        handled.current = true;
        const sessionId = decodeURIComponent(m[1]);
        completeAuthSession(sessionId)
            .then(() => {
                window.history.replaceState(
                    {},
                    "",
                    window.location.pathname + window.location.search,
                );
                navigate("/admin", { replace: true });
            })
            .catch((e) => {
                setErr(
                    e?.response?.data?.detail ||
                        "Sign-in failed. Only @emergent.sh accounts are allowed.",
                );
                window.history.replaceState(
                    {},
                    "",
                    window.location.pathname + window.location.search,
                );
            });
    }, [location, navigate]);

    if (err) {
        return (
            <div className="fixed inset-0 z-[100] grid place-items-center bg-background text-ink p-8">
                <div className="max-w-md text-center">
                    <div className="vc-section-label vc-eyebrow-accent mb-3">
                        Sign-in rejected
                    </div>
                    <h1 className="font-display text-3xl md:text-4xl font-bold mb-6">
                        {err}
                    </h1>
                    <button
                        onClick={() => navigate("/", { replace: true })}
                        className="vc-btn"
                    >
                        Back home
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-background text-ink">
            <div className="vc-section-label">Signing you in…</div>
        </div>
    );
}

function ScrollToTop() {
    // Reset scroll on every route change. Without this, navigating from a
    // scrolled-down homepage to /apply (or any other route) lands the user
    // mid-page on the new route, which feels broken.
    const { pathname } = useLocation();
    useEffect(() => {
        // Cancel Lenis smooth-scroll if running so we land instantly.
        try {
            window.lenis?.scrollTo(0, { immediate: true });
        } catch {}
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    if (location.hash && location.hash.includes("session_id=")) {
        return <AuthHashHandler />;
    }
    return (
        <>
            <ScrollToTop />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/apply" element={<Apply />} />
                <Route path="/check" element={<Status />} />
                <Route path="/check/:refId" element={<Status />} />
                <Route path="/admin" element={<Admin />} />
            </Routes>
        </>
    );
}

function App() {
    return (
        <div className="App vc-grain min-h-screen bg-background text-ink">
            <BrowserRouter>
                <AppRouter />
            </BrowserRouter>
            <Toaster
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        color: "#f4f4f0",
                        borderRadius: 9999,
                        border: "1.5px solid #1c1c1c",
                        fontFamily: "Outfit, sans-serif",
                    },
                }}
            />
        </div>
    );
}

export default App;
